import { z } from 'zod';
import { PointSchema, MultiPolygonSchema } from './geojson';

/**
 * Utility outage feature (Ice Storm Risk → "Power Outages") — served by
 * GET /api/utility-outages. Backed directly by the E.1.9/E.1.10 `utility_outages`
 * table (BC Hydro + Hydro-Québec + Hydro One + NS Power direct/Kubra feeds, plus
 * the municipal LDC adapters: Alectra, ENMAX, FortisAlberta, Hydro Ottawa,
 * NB Power). NO fusion scoring — this is a raw ingested operational signal.
 *
 * The AMBIENT register (operator-cop-doctrine §3B.8): a point marker SIZED by the
 * customers-affected band (shape encodes "an outage"; size encodes magnitude),
 * never pulsing, coloured by status from the context-locked outage palette
 * (styles/outageStyles). Some upstreams also publish a service-area footprint
 * (`service_area_geom`); when present it renders as a ground-clamped polygon
 * beneath the dot. The ice-storm impact picture (§4.5): "where is power out".
 *
 * Two shapes: the LIST endpoint returns the feature with a NULL geometry footprint
 * stripped (the polygon is fetched lazily / rendered from the same row); the DETAIL
 * endpoint (`/:id`) adds the provenance tail (source, source_record_id, observed_at,
 * ingested_at) for the panel footer.
 */

/** Normalized outage status vocabulary (mirrors the utility_outages CHECK). */
export const UTILITY_OUTAGE_STATUSES = ['active', 'crew_dispatched', 'restored', 'unknown'] as const;
export type UtilityOutageStatus = (typeof UTILITY_OUTAGE_STATUSES)[number];

export const UtilityOutagePropertiesSchema = z.object({
  id: z.string().uuid(),
  /** Canonical utility name ('hydro-quebec', 'bc-hydro', 'ENMAX', …). */
  utility: z.string(),
  /** Normalized status — drives the marker colour (paired with a label). */
  status: z.string(),
  /** Affected customer count (may be 0 / masked-small / null for new outages). */
  customers_affected: z.number().nullable(),
  /** Utility's free-text cause category, when published. */
  cause: z.string().nullable(),
  /** Crew-assigned flag where the upstream exposes it (null = unknown). */
  crew_assigned: z.boolean().nullable(),
  /** When the outage was first reported (UTC ISO), when published. */
  started_at: z.string().nullable(),
  /** Utility's published restoration ETA (UTC ISO), when published. */
  restoration_eta: z.string().nullable(),
  /** Upstream snapshot generation time (UTC ISO). */
  observed_at: z.string(),
  /** True when this row carries a service-area footprint polygon to render. */
  has_service_area: z.boolean(),
});
export type UtilityOutageProperties = z.infer<typeof UtilityOutagePropertiesSchema>;

/**
 * The list-feature geometry is a Point (the representative location, always
 * present) — the click target + marker anchor. The optional service-area polygon
 * ships in `service_area` (the shared GeoJSON MultiPolygon schema) so the layer
 * can ground-clamp it beneath the dot without a second fetch.
 */
export const UtilityOutageFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  /** The service-area footprint, when this row publishes one (else null). */
  service_area: MultiPolygonSchema.nullable(),
  properties: UtilityOutagePropertiesSchema,
});
export type UtilityOutageFeature = z.infer<typeof UtilityOutageFeatureSchema>;

export const UtilityOutageFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(UtilityOutageFeatureSchema),
});
export type UtilityOutageFeatureCollection = z.infer<typeof UtilityOutageFeatureCollectionSchema>;

/** The detail endpoint adds the ingest provenance tail for the panel footer. */
export const UtilityOutageDetailSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  service_area: MultiPolygonSchema.nullable(),
  properties: UtilityOutagePropertiesSchema.extend({
    /** Aggregator/source tag ('bchydro-direct', 'kubra-hydro-one', 'ldc-enmax', …). */
    source: z.string(),
    /** The utility's own outage id (provenance). */
    source_record_id: z.string(),
    /** When WE wrote/last-refreshed the row (UTC ISO) — ingest provenance. */
    ingested_at: z.string(),
  }),
});
export type UtilityOutageDetail = z.infer<typeof UtilityOutageDetailSchema>;
